const WmsFormatted = encodeURIComponent("WMS+Operations");
const Supplier = encodeURIComponent("Supplier");
const SupplierWarehouse = encodeURIComponent("Supplier Warehouse");
const SupplierTerminal = encodeURIComponent("Supplier Terminal");

const SHIPPING_LIST_EXPORT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const USER_MANAGEMENT_ROLES = ["Administration"] as const;

export const canExportShippingList = (role: string): boolean =>
  (SHIPPING_LIST_EXPORT_ROLES as readonly string[]).includes(role);

export const canManageUsers = (role: string): boolean =>
  (USER_MANAGEMENT_ROLES as readonly string[]).includes(role);

export const ADMINISTRATION_VIEW_ROLES = [
  "Administration",
  "Operations",
] as const;

export const ADMINISTRATION_FULL_EDIT_ROLES = [
  "Administration",
  "Sales",
] as const;

export const canViewAdministration = (role: string): boolean =>
  (ADMINISTRATION_VIEW_ROLES as readonly string[]).includes(role);

export const canFullEditAdministration = (role: string): boolean =>
  (ADMINISTRATION_FULL_EDIT_ROLES as readonly string[]).includes(role);

export const isOperationsAdministrationRole = (role: string): boolean =>
  role === "Operations";

export const canEditAdministrationEntityTab = (
  role: string,
  isPriceEntity: boolean,
): boolean =>
  canFullEditAdministration(role) ||
  (isOperationsAdministrationRole(role) && isPriceEntity);

export const canEditAdministrationPriceService = (
  role: string,
  route?: string,
): boolean =>
  canFullEditAdministration(role) ||
  (isOperationsAdministrationRole(role) && route === "DELIVERY");

export const OFFER_LIST_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const OFFER_FULL_ACCESS_ROLES = ["Administration", "Sales"] as const;

export const OFFER_SHIPPING_CASE_CREATE_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canViewOfferList = (role: string): boolean =>
  (OFFER_LIST_VIEW_ROLES as readonly string[]).includes(role);

export const canFullAccessOffers = (role: string): boolean =>
  (OFFER_FULL_ACCESS_ROLES as readonly string[]).includes(role);

export const isOperationsOfferRole = (role: string): boolean =>
  role === "Operations";

export const canCreateOffer = (role: string): boolean =>
  canFullAccessOffers(role);

export const canEditOffer = (role: string): boolean =>
  canFullAccessOffers(role);

export const canDuplicateOffer = (role: string): boolean =>
  canFullAccessOffers(role);

export const canDeleteDraftOffer = (role: string): boolean =>
  canFullAccessOffers(role);

export const canDownloadOfferPdf = (role: string): boolean =>
  canFullAccessOffers(role);

export const canViewOfferHistory = (role: string): boolean =>
  canFullAccessOffers(role);

export const canApproveOffer = (role: string): boolean =>
  canFullAccessOffers(role);

export const canCreateShippingCaseFromOffer = (role: string): boolean =>
  (OFFER_SHIPPING_CASE_CREATE_ROLES as readonly string[]).includes(role);

export const shouldHideNonDeliveryOfferPrices = (role: string): boolean =>
  isOperationsOfferRole(role);

export const LITE_WMS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
  "Supplier",
  "Supplier Terminal",
  "Supplier Warehouse",
] as const;

export const LITE_WMS_FULL_ACCESS_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
] as const;

export const canViewLiteWms = (role: string): boolean =>
  (LITE_WMS_VIEW_ROLES as readonly string[]).includes(role);

export const canFullAccessLiteWms = (role: string): boolean =>
  (LITE_WMS_FULL_ACCESS_ROLES as readonly string[]).includes(role);

export const isSupplierTerminalLiteWmsRole = (role: string): boolean =>
  role === "Supplier Terminal";

export const isSupplierWarehouseLiteWmsRole = (role: string): boolean =>
  role === "Supplier Warehouse";

export const isLegacySupplierRole = (role: string): boolean =>
  role === "Supplier";

export const isLiteTmsSupplierRole = (role: string): boolean =>
  isLegacySupplierRole(role) ||
  isSupplierTerminalLiteWmsRole(role) ||
  isSupplierWarehouseLiteWmsRole(role);

export const canEditLiteWmsPackingList = (role: string): boolean =>
  canFullAccessLiteWms(role);

export const canExportLiteWms = (role: string): boolean => canViewLiteWms(role);

export const LITE_TMS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
  "Supplier",
  "Supplier Terminal",
  "Supplier Warehouse",
] as const;

export const LITE_TMS_FULL_ACCESS_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
] as const;

export const LITE_TMS_EXPORT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
  "Supplier",
  "Supplier Terminal",
  "Supplier Warehouse",
] as const;

export const canViewLiteTms = (role: string): boolean =>
  (LITE_TMS_VIEW_ROLES as readonly string[]).includes(role);

export const canFullAccessLiteTms = (role: string): boolean =>
  (LITE_TMS_FULL_ACCESS_ROLES as readonly string[]).includes(role);

export const canExportLiteTms = (role: string): boolean =>
  (LITE_TMS_EXPORT_ROLES as readonly string[]).includes(role);

export const isSupplierTerminalLiteTmsRole = (role: string): boolean =>
  role === "Supplier Terminal";

export const isSupplierWarehouseLiteTmsRole = (role: string): boolean =>
  role === "Supplier Warehouse";

export const isLiteTmsViewOnlyRole = (role: string): boolean =>
  canViewLiteTms(role) && !canFullAccessLiteTms(role);

export const LITE_TMS_WHS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
  "Supplier",
  "Supplier Terminal",
  "Supplier Warehouse",
] as const;

export const LITE_TMS_WHS_FULL_ACCESS_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
] as const;

export const LITE_TMS_WHS_EXPORT_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "WMS+Operations",
  "Supplier",
  "Supplier Terminal",
  "Supplier Warehouse",
] as const;

export const canViewLiteTmsWhs = (role: string): boolean =>
  (LITE_TMS_WHS_VIEW_ROLES as readonly string[]).includes(role);

export const canFullAccessLiteTmsWhs = (role: string): boolean =>
  (LITE_TMS_WHS_FULL_ACCESS_ROLES as readonly string[]).includes(role);

export const canExportLiteTmsWhs = (role: string): boolean =>
  (LITE_TMS_WHS_EXPORT_ROLES as readonly string[]).includes(role);

export const isLiteTmsWhsViewOnlyRole = (role: string): boolean =>
  canViewLiteTmsWhs(role) && !canFullAccessLiteTmsWhs(role);

export const LITE_DELIVERY_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "Customer",
] as const;

export const LITE_DELIVERY_FULL_ACCESS_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canViewLiteDelivery = (role: string): boolean =>
  (LITE_DELIVERY_VIEW_ROLES as readonly string[]).includes(role);

export const canFullAccessLiteDelivery = (role: string): boolean =>
  (LITE_DELIVERY_FULL_ACCESS_ROLES as readonly string[]).includes(role);

export const canExportLiteDelivery = (role: string): boolean =>
  canFullAccessLiteDelivery(role);

export const canOpenLiteDeliveryOrder = (role: string): boolean =>
  canFullAccessLiteDelivery(role);

export const isLiteDeliveryViewOnlyRole = (role: string): boolean =>
  canViewLiteDelivery(role) && !canFullAccessLiteDelivery(role);

export const LITE_DELIVERY_WHS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const canViewLiteDeliveryWhs = (role: string): boolean =>
  (LITE_DELIVERY_WHS_VIEW_ROLES as readonly string[]).includes(role);

export const canExportLiteDeliveryWhs = (role: string): boolean =>
  canViewLiteDeliveryWhs(role);

export const canOpenLiteDeliveryWhsOrder = (role: string): boolean =>
  canViewLiteDeliveryWhs(role);

export const LITE_CUSTOMS_VIEW_ROLES = [
  "Administration",
  "Sales",
  "Operations",
  "Customer",
] as const;

export const LITE_CUSTOMS_FULL_ACCESS_ROLES = [
  "Administration",
  "Sales",
  "Operations",
] as const;

export const LITE_CUSTOMS_EXPORT_ROLES = ["Administration"] as const;

export const canViewLiteCustoms = (role: string): boolean =>
  (LITE_CUSTOMS_VIEW_ROLES as readonly string[]).includes(role);

export const canFullAccessLiteCustoms = (role: string): boolean =>
  (LITE_CUSTOMS_FULL_ACCESS_ROLES as readonly string[]).includes(role);

export const canExportLiteCustoms = (role: string): boolean =>
  (LITE_CUSTOMS_EXPORT_ROLES as readonly string[]).includes(role);

export const canOpenLiteCustomsBill = (role: string): boolean =>
  canViewLiteCustoms(role);

export const isLiteCustomsViewOnlyRole = (role: string): boolean =>
  role === "Customer";

export const tabletAccessMatchers: RegExp[] = [
  /^\/wms\/?$/,
  /^\/tms\/?$/,
  /^\/tms-whs\/?$/,
  /^\/offers\/\d+\/shipping-case\/\d+\/tms\/?$/,
  /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
  /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
  /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
  /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
  /^\/offers\/\d+\/shipping-case\/\d+\/packing-list\/?$/,
  /^\/$/,
];

export const roleAccessMatchers: Record<string, RegExp[]> = {
  Administration: [
    /^\/offers\/?$/,
    /^\/offers\/\d+\/?$/,
    /^\/offers\/create\/?$/,
    /^\/offers\/\d+\/update\/?$/,
    /^\/offers\/\d+\/shipping-case\/(create|\d+)(\/update)?\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/[^/]+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/bills\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/delivery-orders\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/whs-delivery-orders\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/deposit-warehouse\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/selected-services\/(\d+|update)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/canceled-services\/(\d+|update)\/?$/,

    /^\/users\/?$/,
    /^\/settings(\/.*)?$/,
    /^\/shippings\/?$/,
    /^\/delivery\/?$/,
    /^\/whs-delivery\/?$/,
    /^\/wms\/?$/,
    /^\/tms\/?$/,
    /^\/tms-whs\/?$/,
    /^\/customs\/?$/,
    /^\/$/,
  ],
  Sales: [
    /^\/offers\/?$/,
    /^\/offers\/\d+\/?$/,
    /^\/offers\/create\/?$/,
    /^\/offers\/\d+\/update\/?$/,
    /^\/offers\/\d+\/shipping-case\/(create|\d+)(\/update)?\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/[^/]+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/bills\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/delivery-orders\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/deposit-warehouse\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/whs-delivery-orders\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/selected-services\/(\d+|update)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/canceled-services\/(\d+|update)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
    /^\/shippings\/?$/,
    /^\/delivery\/?$/,
    /^\/whs-delivery\/?$/,
    /^\/wms\/?$/,
    /^\/tms\/?$/,
    /^\/tms-whs\/?$/,
    /^\/customs\/?$/,
    /^\/$/,
  ],
  Operations: [
    /^\/offers\/?$/,
    /^\/offers\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/create\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/update\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/(?!update)[^/]+\/[^/]+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/delivery-orders\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/deposit-warehouse\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/whs-delivery-orders\/(\d+|create)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/selected-services\/(\d+|update)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/canceled-services\/(\d+|update)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
    /^\/settings(\/.*)?$/,
    /^\/shippings\/?$/,
    /^\/delivery\/?$/,
    /^\/whs-delivery\/?$/,
    /^\/wms\/?$/,
    /^\/tms\/?$/,
    /^\/tms-whs\/?$/,
    /^\/customs\/?$/,
    /^\/$/,
  ],
  Customer: [
    /^\/offers\/\d+\/shipping-case\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/(packing-list|cargo-details)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/delivery\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/delivery\/(delivery-orders|balance-volume|deposit-warehouse)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/cargo-details\/[^/]+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/unloading\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/unloading\/(deposit-warehouse|depositary-change|picking-service|fcr)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/customs\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/customs\/(control-protocols|radiometric|bills)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/customs\/bills\/\d+\/?$/,
    /^\/shippings\/?$/,
    /^\/delivery\/?$/,
    /^\/customs\/?$/,
    /^\/$/,
  ],
  [Supplier]: [
    /^\/offers\/\d+\/shipping-case\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/packing-list\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
    /^\/tms\/?$/,
    /^\/wms\/?$/,
    /^\/tms-whs\/?$/,
    /^\/$/,
  ],
  [WmsFormatted]: [
    /^\/offers\/\d+\/shipping-case\/\d+\/(packing-list|tms)\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/\d+\/?$/,

    /^\/offers\/\d+\/shipping-case\/\d+\/income-statement\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/canceled-services\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/selected-services\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/[^/]+\/selected-services\/\d+\/?$/,

    /^\/wms\/?$/,
    /^\/tms\/?$/,
    /^\/tms-whs\/?$/,
    /^\/$/,
  ],
  [SupplierWarehouse]: [
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
    /^\/tms\/?$/,
    /^\/wms\/?$/,
    /^\/tms-whs\/?$/,
    /^\/$/,
  ],
  [SupplierTerminal]: [
    /^\/offers\/\d+\/shipping-case\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/packing-list\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/loading-orders\/\d+\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/?$/,
    /^\/offers\/\d+\/shipping-case\/\d+\/tms\/whs-loading-orders\/\d+\/?$/,
    /^\/tms\/?$/,
    /^\/wms\/?$/,
    /^\/tms-whs\/?$/,
    /^\/$/,
  ],
};
